import makeStyles from '@mui/styles/makeStyles';
import { ReactElement, useState } from 'react';
import { useWindowSize } from 'react-use';
import theme, {
  RaptorTheme,
  greys,
  mainColors,
} from '../../../../styling/theme';
import {
  percentageToNoDecimalPlacesNoHundred,
  percentageToTwoDecimalPlacesNoHundred,
} from '../../../../utilities/numberFormatters';
import GenericBarChart from '../../../charts/GenericBarChart';
import GridItem from '../../../layout/GridComponents/GridItem';
import GeneralSelect from '../../../selects/GeneralSelect';
import {
  LasdabCplRiskReport,
  LasdabScenarioTable,
  LasdabTableDataRow,
} from './lasdab.types';

const useStyles = makeStyles<RaptorTheme>((theme) => ({
  pickerRoot: {
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    marginRight: '0.75rem',
    height: '3.12rem',
    width: '100%',
    marginBottom: '0.75rem',
  },
  selectRoot: {
    width: '100%',
  },
}));

interface Props {
  data: LasdabCplRiskReport | null | undefined;
  stressLevel: string;
}

interface chartData {
  name: number;
  dailyPercentage: number;
  cumulativePercentage: number;
}

function buildChartData(
  inputData: LasdabCplRiskReport | null | undefined,
  level: string,
): chartData[] {
  const data = inputData?.lasdab;
  if (!data) return [];

  const stressData: LasdabScenarioTable =
    level === 'stressed'
      ? data.stressed
      : level === 'superStressed'
        ? data.super_stressed
        : data.bau;

  const returnData: chartData[] = [];
  const rows = stressData.slice(1) as LasdabTableDataRow[];
  rows.forEach((value) => {
    returnData.push({
      name: value[0],
      dailyPercentage: value[1],
      cumulativePercentage: value[2],
    });
  });
  return returnData;
}

function buildBarsData(stressLevel: string, dataType: string) {
  switch (stressLevel) {
    case 'standard':
      return [
        {
          dataKey: dataType,
          fill: mainColors.mainBlue,
          key: dataType,
        },
      ];
    case 'stressed':
      return [
        {
          dataKey: dataType,
          fill: mainColors.Alert,
          key: dataType,
        },
      ];
    case 'superStressed':
      return [
        {
          dataKey: dataType,
          fill: mainColors.Fail,
          key: dataType,
        },
      ];
    default:
      return [
        {
          dataKey: dataType,
          fill: mainColors.mainBlue,
          key: dataType,
        },
      ];
  }
}

function LasdabChart({ data, stressLevel }: Props): ReactElement | null {
  const chartData = buildChartData(data, stressLevel);
  const { height } = useWindowSize();
  const [balanceType, setBalanceType] = useState('cumulativePercentage');
  const bars = buildBarsData(stressLevel, balanceType);
  // Set up the select for chosing net or gross exposure
  const setSelected = (value: any) => {
    setBalanceType(value);
  };
  const classes = useStyles();

  return (
    <GridItem xs={9} card>
      <div
        style={{
          float: 'right',
          // height: 500,
          marginRight: '0.62rem',
          marginTop: '0.62rem',
          width: '15%',
        }}
      >
        <GeneralSelect
          labels={['Cumulative Balance', 'Daily Balance']}
          values={['cumulativePercentage', 'dailyPercentage']}
          selected={balanceType}
          setSelected={setSelected}
          placeholderValue=""
          placeholderColor={greys.grey900}
          selectTitle={'Balance Type'}
          customStyles={{
            pickerRoot: classes.pickerRoot,
            selectRoot: classes.selectRoot,
          }}
        />
      </div>
      <GenericBarChart
        data={chartData}
        tooltipFormatter={(value: any, name: any) => [
          percentageToTwoDecimalPlacesNoHundred(value as number),
          `${balanceType === 'cumulativePercentage' ? 'Cumulative' : 'Daily'
          } Balance`,
        ]}
        height={height * 0.7}
        width={'100%'}
        xAxes={[
          {
            tick: {
              fontSize: '0.88rem',
            },
            label: {
              value: '# Days',
              offset: 0,
              position: 'bottom',
              style: {
                fill: theme.palette.grey[700],
              },
            },
            dataKey: 'name',
          },
        ]}
        bars={bars}
        loading={false}
        title={`${balanceType === 'cumulativePercentage' ? 'Cumulative' : 'Daily'
          } Achievable Balance`}
        yAxisFormatter={(value) => percentageToNoDecimalPlacesNoHundred(value)}
      />
    </GridItem>
  );
}

export default LasdabChart;
